<script>
import {
  PUBLIC_ASSETS_URL,
  PUBLIC_SITE_URL,
  PUBLIC_SITE_PROTOCOL,
  PUBLIC_SITE_NAME,
  PUBLIC_SITE_SLOGAN,
} from "$env/static/public";

import { m } from "$lib/paraglide/messages.js";
import { baseLocale } from "$lib/paraglide/runtime";
import { toOgLocale } from "$lib/utils";

let {
  title = "",
  divider = " — ",
  url = "",
  image = "",
  robots = "index,follow",
  description = "",
  keywords = "",
  addNametoTitle = true,
  createdAt = "",
  updatedAt = "",
  publishedAt = "",
  creator = "Ali Samar",
  locale = baseLocale,
  // Twitter Config
  twitterCard = "summary_large_image",
  twitterSite = "@alisamarcom",
  twitterCreator = "@alisamarcom",
  // OG Config
  ogType = "website",
  ogSiteName = "Ali Samar",
  ogImageWidth = "1200",
  ogImageHeight = "675",
  // Breadcrumbs
  breadcrumbs = [],
  breadcrumbItems = [],
  wordCount = 0,
  ldjson = ["website", "organization"], // Default ldjson types
  ldjsonAdditional = [],
  ldjsonTemplate = {},
} = $props();

const createTitle = () => {
  if (addNametoTitle) return [title, PUBLIC_SITE_NAME].join(divider);
  return title;
};

const isExternal = $derived(/^https?:\/\//.test(image));

const metaURL = $derived(
  url ? `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}${url}` : `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}/`,
);
const metaTitle = $derived(title ? createTitle() : PUBLIC_SITE_NAME);
const metaImage = $derived(
  image ? (isExternal ? image : `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_ASSETS_URL}${image}`) : null,
);

const ldjsonTemplates = $derived.by(() => {
  const defaultTemplates = {
    website: {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: metaTitle,
      description: PUBLIC_SITE_SLOGAN,
      url: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}`,
      image: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}/logo.png`,
    },
    webpage: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    collectionpage: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    blog: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    organization: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: PUBLIC_SITE_NAME,
      url: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}`,
      logo: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}/favicon.png`,
    },
    person: {
      "@context": "https://schema.org",
      "@type": "Person",
      name: creator,
      url: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}`,
      image: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}/logo.png`,
      jobTitle: "Freelance Designer & Developer",
      hasOccupation: {
        "@type": "Occupation",
        name: "Freelance Web Developer & Designer",
        occupationalCategory: "15-1254.00",
        skills: "Web Performance, SvelteKit, UI/UX Design, SEO, TypeScript, Frontend Development",
      },
      knowsAbout: [
        "Web Performance Optimization",
        "Core Web Vitals",
        "SvelteKit",
        "Svelte",
        "TypeScript",
        "Frontend Development",
        "SEO",
        "UI/UX Design",
        "Internationalization (i18n)",
      ],
      makesOffer: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Web Performance Optimization",
            description:
              "Core Web Vitals optimization, LCP/INP/CLS improvements, and performance audits for faster websites.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "Frontend Development",
            description:
              "Custom web applications built with SvelteKit, Svelte 5, and TypeScript. Responsive, accessible, and performant.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "UI/UX Design & Development",
            description:
              "Design and development of user interfaces for web and mobile applications with a focus on simplicity and usability.",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "SEO & Structured Data Implementation",
            description:
              "Technical SEO audits, structured data implementation, and search engine optimization for better organic visibility.",
          },
        },
      ],
      sameAs: ["https://twitter.com/alisamarcom", "https://github.com/alisamar"],
    },
    article: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: metaTitle,
      description: description,
      url: metaURL,
      image: metaImage,
      author: {
        "@type": "Person",
        name: creator,
      },
      datePublished: publishedAt,
      dateModified: updatedAt,
    },
    // product: {
    //   "@context": "https://schema.org",
    //   "@type": "Product",
    //   name: metaTitle,
    //   description: description,
    //   image: metaImage,
    //   url: metaURL,
    // },
    contactpage: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    profilepage: {
      "@context": "https://schema.org",
      "@type": "ProfilePage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    searchresultspage: {
      "@context": "https://schema.org",
      "@type": "SearchResultsPage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    privacypage: {
      "@context": "https://schema.org",
      "@type": "PrivacyPage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    termspage: {
      "@context": "https://schema.org",
      "@type": "TearmsPage",
      name: metaTitle,
      description: description,
      url: metaURL,
    },
    breadcrumb: {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: m.footer_nav_home(),
          item: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}`,
        },
        ...breadcrumbItems,
        ...breadcrumbs.map((item, idx) => ({
          "@type": "ListItem",
          position: item.position ?? idx + 2,
          name: item.title,
          item: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}${item.href}`,
        })),
      ],
    },
    faqPage: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: (ldjsonTemplate.faqPage?.questions || []).map((q) => ({
        "@type": "Question",
        name: q.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: q.answer,
        },
      })),
    },
    speakable: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: metaTitle,
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ldjsonTemplate.speakable?.cssSelector || ["article h1", "[data-speakable]"],
      },
      url: metaURL,
    },
    howTo: {
      "@context": "https://schema.org",
      "@type": "HowTo",
      name: metaTitle,
      description: description,
      image: metaImage,
      step: (ldjsonTemplate.howTo?.steps || []).map((s, idx) => ({
        "@type": "HowToStep",
        position: idx + 1,
        name: s.name,
        text: s.text,
        ...(s.url ? { url: s.url } : {}),
      })),
    },
    blogposting: {
      "@context": "https://schema.org",
      "@type": "BlogPosting",
      mainEntityOfPage: {
        "@type": "WebPage",
        "@id": metaURL,
      },
      headline: metaTitle,
      description,
      image: metaImage,
      author: {
        "@type": "Person",
        name: creator,
        url: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}`,
        sameAs: ["https://twitter.com/alisamarcom", "https://github.com/alisamar"],
      },
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["article h1", "article h2", "[data-speakable]"],
      },
      publisher: {
        "@type": "Organization",
        name: PUBLIC_SITE_NAME,
        logo: {
          "@type": "ImageObject",
          url: `${PUBLIC_SITE_PROTOCOL}://${PUBLIC_SITE_URL}/logo.png`,
        },
      },
      datePublished: publishedAt,
      dateModified: updatedAt || publishedAt,
      wordCount: wordCount || 0,
      articleSection: "Blog",
      keywords: keywords,
      url: metaURL,
    },
    product: {
      "@context": "https://schema.org",
      "@type": "Product",
      name: metaTitle,
      description,
      brand: {
        "@type": "Brand",
        name: PUBLIC_SITE_NAME,
      },
      image: metaImage ? [metaImage] : [],
      sku: ldjsonTemplate.product?.sku || "",
      mpn: ldjsonTemplate.product?.mpn || "",
      gtin: ldjsonTemplate.product?.gtin || "",
      url: metaURL,
      // aggregateRating: {
      //   "@type": "AggregateRating",
      //   ratingValue: ldjsonTemplate.product?.ratingValue || "",
      //   reviewCount: ldjsonTemplate.product?.reviewCount || "",
      // },
      // review: ldjsonTemplate.product?.reviews || [],
    },
  };

  // Merge default templates with dynamic template using deepMerge
  return Object.keys(defaultTemplates).reduce(
    (acc, key) => {
      // acc[key] = deepMerge(defaultTemplates[key], ldjsonTemplate[key] || {});
      // Object.assign({}, obj1, obj2);
      acc[key] = Object.assign({}, defaultTemplates[key], ldjsonTemplate[key] || {});
      return acc;
    },
    { ...ldjsonTemplate },
  );
});

// console.log("ldjsonTemplates:", ldjsonTemplates);

const getLdjsonSchema = () => {
  const ld = ldjsonTemplates;
  const ldjsonArray = Array.isArray(ldjson) ? ldjson : [ldjson];
  const ldjsonAdditionalArray = Array.isArray(ldjsonAdditional) ? ldjsonAdditional : [];

  const fromTemplates = ldjsonArray.map((item) => ld[item] ?? null).filter(Boolean);

  const fromAdditional = ldjsonAdditionalArray
    .flatMap((item) => {
      if (typeof item === "string") return ld[item] ?? null;
      if (typeof item === "function") {
        const result = item(ld);
        return Array.isArray(result) ? result : [result];
      }
      return item;
    })
    .filter(Boolean);

  return [...fromTemplates, ...fromAdditional];
};

// Process schema
const processed_schema = getLdjsonSchema();

// Convert processed schema to JSON-LD script
const json_ld_data = `<script type="application/ld+json">${JSON.stringify(processed_schema)}</scr` + `ipt>`;
</script>

<svelte:head>
  <!-- eslint-disable-next-line svelte/no-at-html-tags -->
  {@html "<!-- SEO START -->"}
  {#if metaTitle}
    <title>{metaTitle}</title>
    <meta name="title" property="og:title" content={metaTitle} />
  {/if}

  {#if robots}
    <meta name="robots" content={robots} />
  {/if}

  <meta name="twitter:title" property="twitter:title" content={metaTitle} />
  <meta name="twitter:card" property="twitter:card" content={twitterCard} />
  <meta name="twitter:site" property="twitter:site" content={twitterSite} />
  <meta name="twitter:creator" property="twitter:creator" content={twitterCreator} />

  <!-- authored date -->
  {#if createdAt}<meta name="date.created" content={createdAt} />{/if}
  <!-- published date -->
  {#if publishedAt}<meta name="date.available" content={publishedAt} />{/if}
  <!-- date or dates that CONTENT was changed/updated -->
  {#if updatedAt}<meta name="date.updated" content={updatedAt} />{/if}

  {#if url || metaURL}
    <meta name="og:url" property="og:url" content={metaURL} />
    <link rel="canonical" href={metaURL} />
  {/if}
  <meta name="og:type" property="og:type" content={ogType} />
  <meta name="og:locale" property="og:locale" content={toOgLocale(locale)} />
  <meta name="og:site_name" property="og:site_name" content={ogSiteName} />
  <meta name="dc:creator" property="dc:creator" content={creator} />
  {#if image}
    <meta name="twitter:image" property="twitter:image" content={metaImage} />
    {#if description}
      <meta name="twitter:image:alt" content={description} />
    {/if}
    <meta name="og:image" property="og:image" content={metaImage} />
    <meta name="og:image:width" property="og:image:width" content={ogImageWidth} />
    <meta name="og:image:height" property="og:image:height" content={ogImageHeight} />
  {/if}
  {#if description}
    <meta name="twitter:description" property="twitter:description" content={description} />
    <meta name="description" property="og:description" content={description} />
    <!-- <meta name="description" content="{description}" /> -->
  {/if}

  {#if keywords}
    <meta name="keywords" content={keywords} />
  {/if}

  <!-- eslint-disable-next-line svelte/no-at-html-tags -->
  {@html json_ld_data}
  <!-- eslint-disable-next-line svelte/no-at-html-tags -->
  {@html "<!-- SEO END -->"}
</svelte:head>
